import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  Save, 
  AccountBalance, 
  VpnKey,
  AttachMoney,
  MonetizationOn,
  Receipt,
  Assessment,
  TrendingUp,
  AccountTree as AccountTreeIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import AccountSelector from '../../../components/common/AccountSelector';
import config from '../../../config';
import axios from 'axios';
import { toast } from 'react-toastify';

const translations = {
  ar: {
    title: 'إعدادات المحاسبة',
    subtitle: 'تخصيص القيود والإعدادات المحاسبية وربط الحسابات',
    journalSettings: 'إعدادات القيود',

    accountLinks: 'ربط الحسابات',
    incomeStatementLinks: 'ربط حسابات قائمة الدخل',
    incomeStatementLinksDesc: 'ربط الحسابات لتصنيف قائمة الدخل تلقائياً حسب المعايير الدولية',
    cashFlowLinks: 'ربط حسابات قائمة التدفقات النقدية',
    cashFlowLinksDesc: 'ربط الحسابات لتصنيف قائمة التدفقات النقدية حسب IAS 7',
    journalPrefix: 'بادئة القيد',

    cashParentAccount: 'حساب الصناديق الرئيسي',
    cashParentDesc: 'سيتم عرض الحسابات الفرعية (الصناديق) في سندات القبض والصرف النقدي',
    bankParentAccount: 'حساب البنوك الرئيسي',
    bankParentDesc: 'سيتم عرض الحسابات الفرعية (البنوك) في سندات القبض والصرف بشيك',
    checksParentAccount: 'حساب أوراق القبض الرئيسي',
    checksParentDesc: 'الحساب الرئيسي لأوراق القبض (شيكات مقبوضة) — تظهر حساباته الفرعية في سندات القبض بشيك',
    operatingRevenueParent: 'إيرادات التشغيل',
    operatingRevenueDesc: 'الحساب الرئيسي لإيرادات المبيعات والخدمات التشغيلية',
    costOfOperationsParent: 'تكلفة التشغيل/المبيعات',
    costOfOperationsDesc: 'الحساب الرئيسي لتكلفة البضاعة المباعة ومصاريف التشغيل المباشرة',
    adminExpensesParent: 'مصروفات إدارية وعمومية',
    adminExpensesDesc: 'الحساب الرئيسي للمصروفات الإدارية والعمومية',
    otherIncomeParent: 'إيرادات أخرى',
    otherIncomeDesc: 'الحساب الرئيسي للإيرادات الأخرى (فوائد، استثمارات)',
    otherExpensesParent: 'مصروفات وخسائر أخرى',
    otherExpensesDesc: 'الحساب الرئيسي للمصروفات والخسائر الأخرى غير التشغيلية (05-02)',
    taxParent: 'الزكاة والمساهمات',
    taxParentDesc: 'الحساب الرئيسي للزكاة ومساهمات KFAS وNLST وضريبة الدخل',
    cfReceivables: 'الذمم المدينة',
    cfReceivablesDesc: 'التغيرات في رأس المال العامل - الأنشطة التشغيلية',
    cfInventory: 'المخزون',
    cfInventoryDesc: 'التغيرات في رأس المال العامل - الأنشطة التشغيلية',
    cfPrepaidExpenses: 'المصروفات المقدمة',
    cfPrepaidExpensesDesc: 'التغيرات في رأس المال العامل - الأنشطة التشغيلية',
    cfPayables: 'الذمم الدائنة',
    cfPayablesDesc: 'التغيرات في رأس المال العامل - الأنشطة التشغيلية',
    cfAccruedExpenses: 'المصروفات المستحقة',
    cfAccruedExpensesDesc: 'التغيرات في رأس المال العامل - الأنشطة التشغيلية',
    cfDeferredRevenue: 'الإيرادات المؤجلة',
    cfDeferredRevenueDesc: 'التغيرات في رأس المال العامل - الأنشطة التشغيلية',
    cfDepreciation: 'الاستهلاك والإطفاء',
    cfDepreciationDesc: 'التعديلات غير النقدية - الأنشطة التشغيلية',
    cfProvisions: 'المخصصات',
    cfProvisionsDesc: 'التعديلات غير النقدية - الأنشطة التشغيلية',
    cfInterestReceived: 'الفوائد المقبوضة',
    cfInterestReceivedDesc: 'اختياري: أنشطة تشغيلية أو استثمارية',
    cfDividendsReceived: 'أرباح الأسهم المقبوضة',
    cfDividendsReceivedDesc: 'اختياري: أنشطة تشغيلية أو استثمارية',
    cfFixedAssets: 'الأصول الثابتة',
    cfFixedAssetsDesc: 'الأنشطة الاستثمارية',
    cfIntangibleAssets: 'الأصول غير الملموسة',
    cfIntangibleAssetsDesc: 'الأنشطة الاستثمارية',
    cfInvestments: 'الاستثمارات',
    cfInvestmentsDesc: 'الأنشطة الاستثمارية',
    cfLoansGranted: 'القروض الممنوحة',
    cfLoansGrantedDesc: 'الأنشطة الاستثمارية',
    cfLongTermLoans: 'القروض طويلة الأجل',
    cfLongTermLoansDesc: 'الأنشطة التمويلية',
    cfShortTermLoans: 'القروض قصيرة الأجل',
    cfShortTermLoansDesc: 'الأنشطة التمويلية',
    cfShareCapital: 'رأس المال',
    cfShareCapitalDesc: 'الأنشطة التمويلية',
    cfDividendsPaid: 'التوزيعات المدفوعة',
    cfDividendsPaidDesc: 'الأنشطة التمويلية',
    cfLeasePayments: 'مدفوعات الإيجار (IFRS 16)',
    cfLeasePaymentsDesc: 'الأنشطة التمويلية',
    cfInterestPaid: 'الفوائد المدفوعة',
    cfInterestPaidDesc: 'اختياري: أنشطة تشغيلية أو تمويلية',
    payrollSettings: 'إعدادات الرواتب',
    payrollSettingsDesc: 'ربط حسابات الرواتب لإنشاء القيود المحاسبية تلقائياً',
    adminSalariesAccount: 'حساب الرواتب الإدارية',
    adminSalariesAccountDesc: 'للموظفين الإداريين العامين (بدون مركز تكلفة)',
    operatingSalariesAccount: 'حساب رواتب التشغيل',
    operatingSalariesAccountDesc: 'لموظفي الفروع (مع مركز تكلفة حسب الفرع)',
    bakerySalariesAccount: 'حساب رواتب المخبز',
    bakerySalariesAccountDesc: 'حساب رواتب خاص بفرع المخبز (05-03-02-002)',
    accruedPayrollAccount: 'حساب المصروفات المستحقة',
    accruedPayrollAccountDesc: 'الحساب الدائن عند إثبات الراتب (الالتزام)',
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل حفظ الإعدادات',
    loadError: 'فشل تحميل الإعدادات',
    // Chart of Accounts Settings
    chartOfAccountsSettings: 'إعدادات شجرة الحسابات',
    chartOfAccountsSettingsDesc: 'تحديد عدد الأصفار لكل مستوى في ترقيم الحسابات',
    level: 'المستوى',
    zeros: 'عدد الأصفار',
    zerosExample: 'مثال',
    levelValidationError: 'عدد الأصفار يجب أن يكون أكبر من أو يساوي المستوى السابق',
    // Warehouse Stock Count Settings
    warehouseStockCountSettings: 'إعدادات جرد المخازن',
    warehouseStockCountSettingsDesc: 'ربط حسابات العجز والفائض لإنشاء القيود المحاسبية تلقائياً عند جرد المخازن',
    inventoryShrinkageAccount: 'حساب عجز وتلف المخزون',
    inventoryShrinkageAccountDesc: 'يُسجَّل كمصروف عند وجود عجز في الكمية الفعلية عن كمية النظام',
    inventorySurplusAccount: 'حساب فائض المخزون',
    inventorySurplusAccountDesc: 'يُسجَّل كإيراد أخرى عند وجود زيادة في الكمية الفعلية عن كمية النظام',
    inventoryCostAdjustmentAccount: 'حساب تسوية تكلفة المخزون (تعديل يدوي)',
    inventoryCostAdjustmentAccountDesc: 'يُستخدم فقط عند تعديل تكلفة سند صرف يدوياً (للضرورة القصوى) — يستوعب الفرق بين التكلفة اليدوية وتكلفة FIFO الواقعية',
  },
  en: {
    title: 'Accounting Settings',
    subtitle: 'Customize journal entries, accounting configurations, and account links',
    journalSettings: 'Journal Settings',

    accountLinks: 'Account Links',
    incomeStatementLinks: 'Income Statement Account Links',
    incomeStatementLinksDesc: 'Link accounts for automatic Income Statement classification per international standards',
    cashFlowLinks: 'Cash Flow Statement Account Links',
    cashFlowLinksDesc: 'Link accounts for automatic Cash Flow Statement classification per IAS 7',
    journalPrefix: 'Journal Prefix',

    cashParentAccount: 'Cash Parent Account',
    cashParentDesc: 'Sub-accounts (cash boxes) will be displayed in cash receipts and payments',
    bankParentAccount: 'Bank Parent Account',
    bankParentDesc: 'Sub-accounts (banks) will be displayed in check receipts and payments',
    checksParentAccount: 'Notes Receivable Parent Account',
    checksParentDesc: 'Parent account for Notes Receivable (checks received) — sub-accounts appear in check receipt vouchers',
    operatingRevenueParent: 'Operating Revenue',
    operatingRevenueDesc: 'Parent account for sales and operating service revenue',
    costOfOperationsParent: 'Cost of Operations/Sales',
    costOfOperationsDesc: 'Parent account for cost of goods sold and direct operating expenses',
    adminExpensesParent: 'Administrative & General Expenses',
    adminExpensesDesc: 'Parent account for administrative and general expenses',
    otherIncomeParent: 'Other Income',
    otherIncomeDesc: 'Parent account for other income (interest, investments)',
    otherExpensesParent: 'Other Expenses & Losses',
    otherExpensesDesc: 'Parent account for other non-operating expenses and losses (05-02)',
    taxParent: 'Zakat & Contributions',
    taxParentDesc: 'Parent account for Zakat, KFAS, NLST contributions and income tax',
    cfReceivables: 'Accounts Receivable',
    cfReceivablesDesc: 'Working Capital Changes - Operating Activities',
    cfInventory: 'Inventory',
    cfInventoryDesc: 'Working Capital Changes - Operating Activities',
    cfPrepaidExpenses: 'Prepaid Expenses',
    cfPrepaidExpensesDesc: 'Working Capital Changes - Operating Activities',
    cfPayables: 'Accounts Payable',
    cfPayablesDesc: 'Working Capital Changes - Operating Activities',
    cfAccruedExpenses: 'Accrued Expenses',
    cfAccruedExpensesDesc: 'Working Capital Changes - Operating Activities',
    cfDeferredRevenue: 'Deferred Revenue',
    cfDeferredRevenueDesc: 'Working Capital Changes - Operating Activities',
    cfDepreciation: 'Depreciation & Amortization',
    cfDepreciationDesc: 'Non-Cash Adjustments - Operating Activities',
    cfProvisions: 'Provisions',
    cfProvisionsDesc: 'Non-Cash Adjustments - Operating Activities',
    cfInterestReceived: 'Interest Received',
    cfInterestReceivedDesc: 'Optional: Operating or Investing Activities',
    cfDividendsReceived: 'Dividends Received',
    cfDividendsReceivedDesc: 'Optional: Operating or Investing Activities',
    cfFixedAssets: 'Fixed Assets (PPE)',
    cfFixedAssetsDesc: 'Investing Activities',
    cfIntangibleAssets: 'Intangible Assets',
    cfIntangibleAssetsDesc: 'Investing Activities',
    cfInvestments: 'Investments',
    cfInvestmentsDesc: 'Investing Activities',
    cfLoansGranted: 'Loans Granted',
    cfLoansGrantedDesc: 'Investing Activities',
    cfLongTermLoans: 'Long-term Loans',
    cfLongTermLoansDesc: 'Financing Activities',
    cfShortTermLoans: 'Short-term Loans',
    cfShortTermLoansDesc: 'Financing Activities',
    cfShareCapital: 'Share Capital',
    cfShareCapitalDesc: 'Financing Activities',
    cfDividendsPaid: 'Dividends Paid',
    cfDividendsPaidDesc: 'Financing Activities',
    cfLeasePayments: 'Lease Payments (IFRS 16)',
    cfLeasePaymentsDesc: 'Financing Activities',
    cfInterestPaid: 'Interest Paid',
    cfInterestPaidDesc: 'Optional: Operating or Financing Activities',
    payrollSettings: 'Payroll Settings',
    payrollSettingsDesc: 'Link payroll accounts for automatic journal entry creation',
    adminSalariesAccount: 'Administrative Salaries Account',
    adminSalariesAccountDesc: 'For general administrative employees (without cost center)',
    operatingSalariesAccount: 'Operating Salaries Account',
    operatingSalariesAccountDesc: 'For branch employees (with cost center per branch)',
    bakerySalariesAccount: 'Bakery Salaries Account',
    bakerySalariesAccountDesc: 'Dedicated salary account for Bakery branch (05-03-02-002)',
    accruedPayrollAccount: 'Accrued Payroll Account',
    accruedPayrollAccountDesc: 'Credit account when recording payroll (liability)',
    saveChanges: 'Save Changes',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings',
    loadError: 'Failed to load settings',
    // Chart of Accounts Settings
    chartOfAccountsSettings: 'Chart of Accounts Settings',
    chartOfAccountsSettingsDesc: 'Define the number of leading zeros per level in account numbering',
    level: 'Level',
    zeros: 'Leading Zeros',
    zerosExample: 'Example',
    levelValidationError: 'Leading zeros must be greater than or equal to the previous level',
    // Warehouse Stock Count Settings
    warehouseStockCountSettings: 'Warehouse Stock Count Settings',
    warehouseStockCountSettingsDesc: 'Link shrinkage and surplus accounts for automatic journal entries when counting warehouse inventory',
    inventoryShrinkageAccount: 'Inventory Shrinkage/Loss Account',
    inventoryShrinkageAccountDesc: 'Recorded as expense when physical quantity is less than system quantity',
    inventorySurplusAccount: 'Inventory Surplus Account',
    inventorySurplusAccountDesc: 'Recorded as other income when physical quantity exceeds system quantity',
    inventoryCostAdjustmentAccount: 'Inventory Cost Adjustment Account (Manual Override)',
    inventoryCostAdjustmentAccountDesc: 'Used only when stock-issue cost is manually overridden (emergency use) — absorbs the difference between manual cost and actual FIFO cost',
  }
};

// Helper: generate example code based on zeros count
const getZerosExample = (zeros: number): string => {
  // zeros = number of leading zeros, so total digits = zeros + 1
  // e.g. zeros=0 → "1", zeros=1 → "01", zeros=2 → "001", zeros=5 → "000001"
  const totalDigits = zeros + 1;
  return '0'.repeat(zeros) + '1';
};

export default function AccountingSettingsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    journalPrefix: 'JV',

  });

  const [cashParentAccountId, setCashParentAccountId] = useState<number | null>(null);
  const [bankParentAccountId, setBankParentAccountId] = useState<number | null>(null);
  const [checksParentAccountId, setChecksParentAccountId] = useState<number | null>(null);
  
  // Income Statement Account Links
  const [operatingRevenueParentId, setOperatingRevenueParentId] = useState<number | null>(null);
  const [costOfOperationsParentId, setCostOfOperationsParentId] = useState<number | null>(null);
  const [adminExpensesParentId, setAdminExpensesParentId] = useState<number | null>(null);
  const [otherIncomeParentId, setOtherIncomeParentId] = useState<number | null>(null);
  const [otherExpensesParentId, setOtherExpensesParentId] = useState<number | null>(null);
  const [zakatContributionsParentId, setZakatContributionsParentId] = useState<number | null>(null);

  // Cash Flow Statement Account Links (20 settings)
  const [cfReceivablesId, setCfReceivablesId] = useState<number | null>(null);
  const [cfInventoryId, setCfInventoryId] = useState<number | null>(null);
  const [cfPrepaidExpensesId, setCfPrepaidExpensesId] = useState<number | null>(null);
  const [cfPayablesId, setCfPayablesId] = useState<number | null>(null);
  const [cfAccruedExpensesId, setCfAccruedExpensesId] = useState<number | null>(null);
  const [cfDeferredRevenueId, setCfDeferredRevenueId] = useState<number | null>(null);
  const [cfDepreciationId, setCfDepreciationId] = useState<number | null>(null);
  const [cfProvisionsId, setCfProvisionsId] = useState<number | null>(null);
  const [cfInterestReceivedId, setCfInterestReceivedId] = useState<number | null>(null);
  const [cfDividendsReceivedId, setCfDividendsReceivedId] = useState<number | null>(null);
  const [cfFixedAssetsId, setCfFixedAssetsId] = useState<number | null>(null);
  const [cfIntangibleAssetsId, setCfIntangibleAssetsId] = useState<number | null>(null);
  const [cfInvestmentsId, setCfInvestmentsId] = useState<number | null>(null);
  const [cfLoansGrantedId, setCfLoansGrantedId] = useState<number | null>(null);
  const [cfLongTermLoansId, setCfLongTermLoansId] = useState<number | null>(null);
  const [cfShortTermLoansId, setCfShortTermLoansId] = useState<number | null>(null);
  const [cfShareCapitalId, setCfShareCapitalId] = useState<number | null>(null);
  const [cfDividendsPaidId, setCfDividendsPaidId] = useState<number | null>(null);
  const [cfLeasePaymentsId, setCfLeasePaymentsId] = useState<number | null>(null);
  const [cfInterestPaidId, setCfInterestPaidId] = useState<number | null>(null);
  
  // Payroll Settings
  const [adminSalariesAccountId, setAdminSalariesAccountId] = useState<number | null>(null);
  const [operatingSalariesAccountId, setOperatingSalariesAccountId] = useState<number | null>(null);
  const [bakerySalariesAccountId, setBakerySalariesAccountId] = useState<number | null>(null);
  const [accruedPayrollAccountId, setAccruedPayrollAccountId] = useState<number | null>(null);

  // Chart of Accounts - Account Code Zeros per Level
  const [accountCodeZeros, setAccountCodeZeros] = useState<number[]>([1, 2, 3, 4, 5, 6]);

  // Warehouse Stock Count Settings
  const [inventoryShrinkageAccountId, setInventoryShrinkageAccountId] = useState<number | null>(null);
  const [inventorySurplusAccountId, setInventorySurplusAccountId] = useState<number | null>(null);
  const [inventoryCostAdjustmentAccountId, setInventoryCostAdjustmentAccountId] = useState<number | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(`${config.API_BASE_URL}/SystemSettings/category/Accounting`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const settings = response.data;

      if (settings.journalPrefix) {
        setFormData(prev => ({ ...prev, journalPrefix: settings.journalPrefix }));
      }
      // Load Voucher Account Links
      if (settings.CashParentAccountId) setCashParentAccountId(parseInt(settings.CashParentAccountId));
      if (settings.BankParentAccountId) setBankParentAccountId(parseInt(settings.BankParentAccountId));
      if (settings.ChecksParentAccountId) setChecksParentAccountId(parseInt(settings.ChecksParentAccountId));

      // Load Income Statement Account Links
      if (settings.OperatingRevenueParentId) setOperatingRevenueParentId(parseInt(settings.OperatingRevenueParentId));
      if (settings.CostOfOperationsParentId) setCostOfOperationsParentId(parseInt(settings.CostOfOperationsParentId));
      if (settings.AdminExpensesParentId) setAdminExpensesParentId(parseInt(settings.AdminExpensesParentId));
      if (settings.OtherIncomeParentId) setOtherIncomeParentId(parseInt(settings.OtherIncomeParentId));
      if (settings.OtherExpensesParentId) setOtherExpensesParentId(parseInt(settings.OtherExpensesParentId));
      if (settings.ZakatContributionsParentId) setZakatContributionsParentId(parseInt(settings.ZakatContributionsParentId));

      // Load Cash Flow Statement Account Links
      if (settings.CFReceivablesParentId) setCfReceivablesId(parseInt(settings.CFReceivablesParentId));
      if (settings.CFInventoryParentId) setCfInventoryId(parseInt(settings.CFInventoryParentId));
      if (settings.CFPrepaidExpensesParentId) setCfPrepaidExpensesId(parseInt(settings.CFPrepaidExpensesParentId));
      if (settings.CFPayablesParentId) setCfPayablesId(parseInt(settings.CFPayablesParentId));
      if (settings.CFAccruedExpensesParentId) setCfAccruedExpensesId(parseInt(settings.CFAccruedExpensesParentId));
      if (settings.CFDeferredRevenueParentId) setCfDeferredRevenueId(parseInt(settings.CFDeferredRevenueParentId));
      if (settings.CFDepreciationParentId) setCfDepreciationId(parseInt(settings.CFDepreciationParentId));
      if (settings.CFProvisionsParentId) setCfProvisionsId(parseInt(settings.CFProvisionsParentId));
      if (settings.CFInterestReceivedParentId) setCfInterestReceivedId(parseInt(settings.CFInterestReceivedParentId));
      if (settings.CFDividendsReceivedParentId) setCfDividendsReceivedId(parseInt(settings.CFDividendsReceivedParentId));
      if (settings.CFFixedAssetsParentId) setCfFixedAssetsId(parseInt(settings.CFFixedAssetsParentId));
      if (settings.CFIntangibleAssetsParentId) setCfIntangibleAssetsId(parseInt(settings.CFIntangibleAssetsParentId));
      if (settings.CFInvestmentsParentId) setCfInvestmentsId(parseInt(settings.CFInvestmentsParentId));
      if (settings.CFLoansGrantedParentId) setCfLoansGrantedId(parseInt(settings.CFLoansGrantedParentId));
      if (settings.CFLongTermLoansParentId) setCfLongTermLoansId(parseInt(settings.CFLongTermLoansParentId));
      if (settings.CFShortTermLoansParentId) setCfShortTermLoansId(parseInt(settings.CFShortTermLoansParentId));
      if (settings.CFShareCapitalParentId) setCfShareCapitalId(parseInt(settings.CFShareCapitalParentId));
      if (settings.CFDividendsPaidParentId) setCfDividendsPaidId(parseInt(settings.CFDividendsPaidParentId));
      if (settings.CFLeasePaymentsParentId) setCfLeasePaymentsId(parseInt(settings.CFLeasePaymentsParentId));
      if (settings.CFInterestPaidParentId) setCfInterestPaidId(parseInt(settings.CFInterestPaidParentId));
      
      // Load Payroll Settings
      if (settings.AdminSalariesAccountId) setAdminSalariesAccountId(parseInt(settings.AdminSalariesAccountId));
      if (settings.OperatingSalariesAccountId) setOperatingSalariesAccountId(parseInt(settings.OperatingSalariesAccountId));
      if (settings.BakerySalariesAccountId) setBakerySalariesAccountId(parseInt(settings.BakerySalariesAccountId));
      if (settings.AccruedPayrollAccountId) setAccruedPayrollAccountId(parseInt(settings.AccruedPayrollAccountId));

      // Load Warehouse Stock Count Settings
      if (settings.InventoryShrinkageAccountId) setInventoryShrinkageAccountId(parseInt(settings.InventoryShrinkageAccountId));
      if (settings.InventorySurplusAccountId) setInventorySurplusAccountId(parseInt(settings.InventorySurplusAccountId));
      if (settings.InventoryCostAdjustmentAccountId) setInventoryCostAdjustmentAccountId(parseInt(settings.InventoryCostAdjustmentAccountId));

      // Load Chart of Accounts Zeros Settings
      try {
        const coaResponse = await axios.get(`${config.API_BASE_URL}/SystemSettings/category/ChartOfAccounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const coaSettings = coaResponse.data;
        const loadedZeros = [...accountCodeZeros];
        for (let i = 1; i <= 6; i++) {
          const val = coaSettings[`AccountCodeZeros_Level${i}`];
          if (val !== undefined && val !== null) {
            loadedZeros[i - 1] = parseInt(val);
          }
        }
        setAccountCodeZeros(loadedZeros);
      } catch (coaErr) {
        console.warn('Chart of accounts settings not found, using defaults');
      }

    } catch (error: any) {
      console.error('Failed to load settings:', error);
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleZerosChange = (levelIndex: number, newValue: number) => {
    setAccountCodeZeros(prev => {
      const updated = [...prev];
      updated[levelIndex] = newValue;
      // Enforce: each next level must be >= current level
      for (let i = levelIndex + 1; i < updated.length; i++) {
        if (updated[i] < updated[i - 1]) {
          updated[i] = updated[i - 1];
        }
      }
      return updated;
    });
  };

  // Get min zeros allowed for a level (must be >= previous level)
  const getMinZeros = (levelIndex: number): number => {
    if (levelIndex === 0) return 0;
    return accountCodeZeros[levelIndex - 1];
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const token = localStorage.getItem('token');
      const settingsToSave: { [key: string]: string } = {};

      settingsToSave['journalPrefix'] = formData.journalPrefix;

      // Voucher Account Links
      settingsToSave['CashParentAccountId'] = cashParentAccountId ? cashParentAccountId.toString() : '';
      settingsToSave['BankParentAccountId'] = bankParentAccountId ? bankParentAccountId.toString() : '';
      settingsToSave['ChecksParentAccountId'] = checksParentAccountId ? checksParentAccountId.toString() : '';

      // Income Statement Account Links
      settingsToSave['OperatingRevenueParentId'] = operatingRevenueParentId ? operatingRevenueParentId.toString() : '';
      settingsToSave['CostOfOperationsParentId'] = costOfOperationsParentId ? costOfOperationsParentId.toString() : '';
      settingsToSave['AdminExpensesParentId'] = adminExpensesParentId ? adminExpensesParentId.toString() : '';
      settingsToSave['OtherIncomeParentId'] = otherIncomeParentId ? otherIncomeParentId.toString() : '';
      settingsToSave['OtherExpensesParentId'] = otherExpensesParentId ? otherExpensesParentId.toString() : '';
      settingsToSave['ZakatContributionsParentId'] = zakatContributionsParentId ? zakatContributionsParentId.toString() : '';

      // Cash Flow Statement Account Links
      settingsToSave['CFReceivablesParentId'] = cfReceivablesId ? cfReceivablesId.toString() : '';
      settingsToSave['CFInventoryParentId'] = cfInventoryId ? cfInventoryId.toString() : '';
      settingsToSave['CFPrepaidExpensesParentId'] = cfPrepaidExpensesId ? cfPrepaidExpensesId.toString() : '';
      settingsToSave['CFPayablesParentId'] = cfPayablesId ? cfPayablesId.toString() : '';
      settingsToSave['CFAccruedExpensesParentId'] = cfAccruedExpensesId ? cfAccruedExpensesId.toString() : '';
      settingsToSave['CFDeferredRevenueParentId'] = cfDeferredRevenueId ? cfDeferredRevenueId.toString() : '';
      settingsToSave['CFDepreciationParentId'] = cfDepreciationId ? cfDepreciationId.toString() : '';
      settingsToSave['CFProvisionsParentId'] = cfProvisionsId ? cfProvisionsId.toString() : '';
      settingsToSave['CFInterestReceivedParentId'] = cfInterestReceivedId ? cfInterestReceivedId.toString() : '';
      settingsToSave['CFDividendsReceivedParentId'] = cfDividendsReceivedId ? cfDividendsReceivedId.toString() : '';
      settingsToSave['CFFixedAssetsParentId'] = cfFixedAssetsId ? cfFixedAssetsId.toString() : '';
      settingsToSave['CFIntangibleAssetsParentId'] = cfIntangibleAssetsId ? cfIntangibleAssetsId.toString() : '';
      settingsToSave['CFInvestmentsParentId'] = cfInvestmentsId ? cfInvestmentsId.toString() : '';
      settingsToSave['CFLoansGrantedParentId'] = cfLoansGrantedId ? cfLoansGrantedId.toString() : '';
      settingsToSave['CFLongTermLoansParentId'] = cfLongTermLoansId ? cfLongTermLoansId.toString() : '';
      settingsToSave['CFShortTermLoansParentId'] = cfShortTermLoansId ? cfShortTermLoansId.toString() : '';
      settingsToSave['CFShareCapitalParentId'] = cfShareCapitalId ? cfShareCapitalId.toString() : '';
      settingsToSave['CFDividendsPaidParentId'] = cfDividendsPaidId ? cfDividendsPaidId.toString() : '';
      settingsToSave['CFLeasePaymentsParentId'] = cfLeasePaymentsId ? cfLeasePaymentsId.toString() : '';
      settingsToSave['CFInterestPaidParentId'] = cfInterestPaidId ? cfInterestPaidId.toString() : '';
      
      // Payroll Settings
      settingsToSave['AdminSalariesAccountId'] = adminSalariesAccountId ? adminSalariesAccountId.toString() : '';
      settingsToSave['OperatingSalariesAccountId'] = operatingSalariesAccountId ? operatingSalariesAccountId.toString() : '';
      settingsToSave['BakerySalariesAccountId'] = bakerySalariesAccountId ? bakerySalariesAccountId.toString() : '';
      settingsToSave['AccruedPayrollAccountId'] = accruedPayrollAccountId ? accruedPayrollAccountId.toString() : '';

      // Warehouse Stock Count Settings
      settingsToSave['InventoryShrinkageAccountId'] = inventoryShrinkageAccountId ? inventoryShrinkageAccountId.toString() : '';
      settingsToSave['InventorySurplusAccountId'] = inventorySurplusAccountId ? inventorySurplusAccountId.toString() : '';
      settingsToSave['InventoryCostAdjustmentAccountId'] = inventoryCostAdjustmentAccountId ? inventoryCostAdjustmentAccountId.toString() : '';

      // Chart of Accounts Zeros Settings
      for (let i = 1; i <= 6; i++) {
        settingsToSave[`AccountCodeZeros_Level${i}`] = accountCodeZeros[i - 1].toString();
      }

      await axios.put(`${config.API_BASE_URL}/SystemSettings`, settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t.saveSuccess);
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      toast.error(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          color: '#333',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(168, 237, 234, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <AccountBalance sx={{ fontSize: 40, color: '#00796b' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="#00796b">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack spacing={3}>

        {/* ============================================================ */}
        {/* Chart of Accounts Settings Card - NEW */}
        {/* ============================================================ */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '2px solid',
            borderColor: 'rgba(255, 152, 0, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(255, 152, 0, 0.2)',
              borderColor: '#ff9800',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <AccountTreeIcon sx={{ color: '#ff9800', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600" color="#ff9800">
                  {t.chartOfAccountsSettings}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t.chartOfAccountsSettingsDesc}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={2}>
              {accountCodeZeros.map((zeros, index) => {
                const levelNum = index + 1;
                const minZeros = getMinZeros(index);
                const example = getZerosExample(zeros);
                const fullExample = index === 0 
                  ? example 
                  : `${getZerosExample(accountCodeZeros[0])}-${index >= 1 ? getZerosExample(accountCodeZeros[1]) : ''}${index >= 2 ? '-' + example : ''}`;
                const simpleExample = example;

                return (
                  <Box 
                    key={levelNum}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: index % 2 === 0 ? 'rgba(255, 152, 0, 0.04)' : 'rgba(255, 152, 0, 0.08)',
                      border: '1px solid rgba(255, 152, 0, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* Level Label */}
                    <Box 
                      sx={{ 
                        minWidth: 100,
                        bgcolor: '#ff9800', 
                        color: '#fff', 
                        px: 2, 
                        py: 0.8, 
                        borderRadius: 1.5, 
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                      }}
                    >
                      {t.level} {levelNum}
                    </Box>

                    {/* Zeros Dropdown */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>{t.zeros}</InputLabel>
                      <Select
                        value={zeros}
                        label={t.zeros}
                        onChange={(e) => handleZerosChange(index, Number(e.target.value))}
                      >
                        {[0, 1, 2, 3, 4, 5, 6].map((z) => (
                          <MenuItem key={z} value={z} disabled={z < minZeros}>
                            {z} {isRTL ? 'أصفار' : 'zeros'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Example */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t.zerosExample}:
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold" 
                        color="#ff9800"
                        sx={{ 
                          bgcolor: 'rgba(255, 152, 0, 0.1)', 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '1.05rem',
                          letterSpacing: 1,
                        }}
                      >
                        {simpleExample}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        {/* Journal Settings Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(0, 121, 107, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <VpnKey sx={{ color: '#00796b', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.journalSettings}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t.journalPrefix}
                name="journalPrefix"
                value={formData.journalPrefix}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

            </Stack>
          </CardContent>
        </Card>

        {/* Account Links Card - Vouchers */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '2px solid',
            borderColor: 'rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(102, 126, 234, 0.2)',
              borderColor: '#667eea',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <AccountBalance sx={{ color: '#667eea', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600" color="#667eea">
                  {t.accountLinks}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {isRTL ? 'ربط الحسابات المحاسبية بالسندات المختلفة' : 'Link accounting accounts to different vouchers'}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={3}>
              {/* Cash Parent Account */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(17, 153, 142, 0.05)',
                  border: '1px solid rgba(17, 153, 142, 0.2)'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <MonetizationOn sx={{ fontSize: 24, color: '#11998e' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#11998e">
                      {t.cashParentAccount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.cashParentDesc}
                    </Typography>
                  </Box>
                </Stack>
                <AccountSelector
                  value={cashParentAccountId}
                  onChange={setCashParentAccountId}
                  label={t.cashParentAccount}
                  onlyFinalAccounts={false}
                />
              </Box>

              <Divider />

              {/* Bank Parent Account */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(102, 126, 234, 0.05)',
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <AccountBalance sx={{ fontSize: 24, color: '#667eea' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#667eea">
                      {t.bankParentAccount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.bankParentDesc}
                    </Typography>
                  </Box>
                </Stack>
                <AccountSelector
                  value={bankParentAccountId}
                  onChange={setBankParentAccountId}
                  label={t.bankParentAccount}
                  onlyFinalAccounts={false}
                />
              </Box>

              <Divider />

              {/* Checks Parent Account */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(118, 75, 162, 0.05)',
                  border: '1px solid rgba(118, 75, 162, 0.2)'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <Receipt sx={{ fontSize: 24, color: '#764ba2' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#764ba2">
                      {t.checksParentAccount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.checksParentDesc}
                    </Typography>
                  </Box>
                </Stack>
                <AccountSelector
                  value={checksParentAccountId}
                  onChange={setChecksParentAccountId}
                  label={t.checksParentAccount}
                  onlyFinalAccounts={false}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Income Statement Account Links Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '2px solid',
            borderColor: 'rgba(233, 30, 99, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(233, 30, 99, 0.2)',
              borderColor: '#e91e63',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(233, 30, 99, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <WarehouseIcon sx={{ color: '#e91e63', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600" color="#e91e63">
                  {t.warehouseStockCountSettings}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t.warehouseStockCountSettingsDesc}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={3}>
              {/* Inventory Shrinkage Account */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(244, 67, 54, 0.05)',
                  border: '1px solid rgba(244, 67, 54, 0.2)'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <TrendingUp sx={{ fontSize: 24, color: '#f44336', transform: 'rotate(180deg)' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#f44336">
                      {t.inventoryShrinkageAccount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.inventoryShrinkageAccountDesc}
                    </Typography>
                  </Box>
                </Stack>
                <AccountSelector
                  value={inventoryShrinkageAccountId}
                  onChange={setInventoryShrinkageAccountId}
                  label={t.inventoryShrinkageAccount}
                  onlyFinalAccounts={true}
                />
              </Box>

              <Divider />

              {/* Inventory Surplus Account */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(76, 175, 80, 0.05)',
                  border: '1px solid rgba(76, 175, 80, 0.2)'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <TrendingUp sx={{ fontSize: 24, color: '#4caf50' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#4caf50">
                      {t.inventorySurplusAccount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.inventorySurplusAccountDesc}
                    </Typography>
                  </Box>
                </Stack>
                <AccountSelector
                  value={inventorySurplusAccountId}
                  onChange={setInventorySurplusAccountId}
                  label={t.inventorySurplusAccount}
                  onlyFinalAccounts={true}
                />
              </Box>

              <Divider />

              {/* Inventory Cost Adjustment Account (Manual Override) */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 152, 0, 0.05)',
                  border: '1px solid rgba(255, 152, 0, 0.3)'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <TrendingUp sx={{ fontSize: 24, color: '#ff9800' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#ff9800">
                      {t.inventoryCostAdjustmentAccount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.inventoryCostAdjustmentAccountDesc}
                    </Typography>
                  </Box>
                </Stack>
                <AccountSelector
                  value={inventoryCostAdjustmentAccountId}
                  onChange={setInventoryCostAdjustmentAccountId}
                  label={t.inventoryCostAdjustmentAccount}
                  onlyFinalAccounts={true}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Income Statement Account Links Card - ORIGINAL */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '2px solid',
            borderColor: 'rgba(76, 175, 80, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(76, 175, 80, 0.2)',
              borderColor: '#4caf50',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(76, 175, 80, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <Assessment sx={{ color: '#4caf50', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600" color="#4caf50">
                  {t.incomeStatementLinks}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t.incomeStatementLinksDesc}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={3}>
              {/* Operating Revenue */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <TrendingUp sx={{ fontSize: 24, color: '#4caf50' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#4caf50">{t.operatingRevenueParent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.operatingRevenueDesc}</Typography>
                  </Box>
                </Stack>
                <AccountSelector value={operatingRevenueParentId} onChange={setOperatingRevenueParentId} label={t.operatingRevenueParent} onlyFinalAccounts={false} />
              </Box>

              <Divider />

              {/* Cost of Operations */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', border: '1px solid rgba(255, 152, 0, 0.2)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <MonetizationOn sx={{ fontSize: 24, color: '#ff9800' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#ff9800">{t.costOfOperationsParent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.costOfOperationsDesc}</Typography>
                  </Box>
                </Stack>
                <AccountSelector value={costOfOperationsParentId} onChange={setCostOfOperationsParentId} label={t.costOfOperationsParent} onlyFinalAccounts={false} />
              </Box>

              <Divider />

              {/* Administrative Expenses */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <AttachMoney sx={{ fontSize: 24, color: '#f44336' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#f44336">{t.adminExpensesParent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.adminExpensesDesc}</Typography>
                  </Box>
                </Stack>
                <AccountSelector value={adminExpensesParentId} onChange={setAdminExpensesParentId} label={t.adminExpensesParent} onlyFinalAccounts={false} />
              </Box>

              <Divider />

              {/* Other Income */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <TrendingUp sx={{ fontSize: 24, color: '#2196f3' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#2196f3">{t.otherIncomeParent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.otherIncomeDesc}</Typography>
                  </Box>
                </Stack>
                <AccountSelector value={otherIncomeParentId} onChange={setOtherIncomeParentId} label={t.otherIncomeParent} onlyFinalAccounts={false} />
              </Box>

              <Divider />

              {/* Other Expenses & Losses */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', border: '1px solid rgba(255, 152, 0, 0.2)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <TrendingUp sx={{ fontSize: 24, color: '#ff9800' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#ff9800">{t.otherExpensesParent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.otherExpensesDesc}</Typography>
                  </Box>
                </Stack>
                <AccountSelector value={otherExpensesParentId} onChange={setOtherExpensesParentId} label={t.otherExpensesParent} onlyFinalAccounts={false} />
              </Box>

              <Divider />

              {/* Zakat & Contributions */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', border: '1px solid rgba(156, 39, 176, 0.2)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <Receipt sx={{ fontSize: 24, color: '#9c27b0' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600" color="#9c27b0">{t.taxParent}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.taxParentDesc}</Typography>
                  </Box>
                </Stack>
                <AccountSelector value={zakatContributionsParentId} onChange={setZakatContributionsParentId} label={t.taxParent} onlyFinalAccounts={false} />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Cash Flow Statement Account Links Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', p: 1.5, borderRadius: 2 }}>
                <TrendingUp sx={{ color: '#2196f3', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">{t.cashFlowLinks}</Typography>
                <Typography variant="caption" color="text.secondary">{t.cashFlowLinksDesc}</Typography>
              </Box>
            </Stack>

            <Stack spacing={2.5}>
              <Box sx={{ bgcolor: 'rgba(76, 175, 80, 0.08)', p: 1.5, borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="600" color="#4caf50">
                  {language === 'ar' ? 'الأنشطة التشغيلية' : 'Operating Activities'}
                </Typography>
              </Box>
              <AccountSelector value={cfReceivablesId} onChange={setCfReceivablesId} label={t.cfReceivables} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfInventoryId} onChange={setCfInventoryId} label={t.cfInventory} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfPrepaidExpensesId} onChange={setCfPrepaidExpensesId} label={t.cfPrepaidExpenses} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfPayablesId} onChange={setCfPayablesId} label={t.cfPayables} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfAccruedExpensesId} onChange={setCfAccruedExpensesId} label={t.cfAccruedExpenses} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfDeferredRevenueId} onChange={setCfDeferredRevenueId} label={t.cfDeferredRevenue} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfDepreciationId} onChange={setCfDepreciationId} label={t.cfDepreciation} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfProvisionsId} onChange={setCfProvisionsId} label={t.cfProvisions} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfInterestReceivedId} onChange={setCfInterestReceivedId} label={t.cfInterestReceived} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfDividendsReceivedId} onChange={setCfDividendsReceivedId} label={t.cfDividendsReceived} onlyFinalAccounts={false} />

              <Box sx={{ bgcolor: 'rgba(156, 39, 176, 0.08)', p: 1.5, borderRadius: 1, mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" color="#9c27b0">
                  {language === 'ar' ? 'الأنشطة الاستثمارية' : 'Investing Activities'}
                </Typography>
              </Box>
              <AccountSelector value={cfFixedAssetsId} onChange={setCfFixedAssetsId} label={t.cfFixedAssets} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfIntangibleAssetsId} onChange={setCfIntangibleAssetsId} label={t.cfIntangibleAssets} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfInvestmentsId} onChange={setCfInvestmentsId} label={t.cfInvestments} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfLoansGrantedId} onChange={setCfLoansGrantedId} label={t.cfLoansGranted} onlyFinalAccounts={false} />

              <Box sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)', p: 1.5, borderRadius: 1, mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" color="#ff9800">
                  {language === 'ar' ? 'الأنشطة التمويلية' : 'Financing Activities'}
                </Typography>
              </Box>
              <AccountSelector value={cfLongTermLoansId} onChange={setCfLongTermLoansId} label={t.cfLongTermLoans} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfShortTermLoansId} onChange={setCfShortTermLoansId} label={t.cfShortTermLoans} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfShareCapitalId} onChange={setCfShareCapitalId} label={t.cfShareCapital} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfDividendsPaidId} onChange={setCfDividendsPaidId} label={t.cfDividendsPaid} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfLeasePaymentsId} onChange={setCfLeasePaymentsId} label={t.cfLeasePayments} onlyFinalAccounts={false} />
              <Divider />
              <AccountSelector value={cfInterestPaidId} onChange={setCfInterestPaidId} label={t.cfInterestPaid} onlyFinalAccounts={false} />
            </Stack>
          </CardContent>
        </Card>


        {/* Payroll Settings Card */}
        <Card 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  bgcolor: 'rgba(139, 195, 74, 0.1)', 
                  p: 1.5, 
                  borderRadius: 2 
                }}
              >
                <MonetizationOn sx={{ color: '#689f38', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {t.payrollSettings}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.payrollSettingsDesc}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={2}>
              <AccountSelector 
                value={adminSalariesAccountId} 
                onChange={setAdminSalariesAccountId} 
                label={t.adminSalariesAccount}
                helperText={t.adminSalariesAccountDesc}
                onlyFinalAccounts={true}
              />
              <Divider />
              <AccountSelector
                value={operatingSalariesAccountId}
                onChange={setOperatingSalariesAccountId}
                label={t.operatingSalariesAccount}
                helperText={t.operatingSalariesAccountDesc}
                onlyFinalAccounts={true}
              />
              <Divider />
              <AccountSelector
                value={bakerySalariesAccountId}
                onChange={setBakerySalariesAccountId}
                label={t.bakerySalariesAccount}
                helperText={t.bakerySalariesAccountDesc}
                onlyFinalAccounts={true}
              />
              <Divider />
              <AccountSelector
                value={accruedPayrollAccountId} 
                onChange={setAccruedPayrollAccountId} 
                label={t.accruedPayrollAccount}
                helperText={t.accruedPayrollAccountDesc}
                onlyFinalAccounts={true}
              />
            </Stack>
          </CardContent>
        </Card>

      </Stack>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#00796b',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(168, 237, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #91dbd8 0%, #f7c0d1 100%)',
              boxShadow: '0 6px 20px rgba(168, 237, 234, 0.6)',
            },
            '&:disabled': {
              background: 'rgba(168, 237, 234, 0.5)',
              color: '#00796b'
            }
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}